import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Rate limiting storage
const rateLimits = {
  tasks: new Map(), // user_id -> { count, resetTime }
  shares: new Map(), // user_id -> { count, resetTime }
  passwordReset: new Map(), // email -> { count, resetTime }
  ipSignup: new Map(), // ip -> { count, resetTime }
}

// Rate limiting function
const checkRateLimit = (userId, action, limit = 10, windowMs = 60000) => {
  const now = Date.now()
  const key = userId
  const store = rateLimits[action]
  
  if (!store) return true // Unknown action, allow
  
  const userLimit = store.get(key)
  
  if (!userLimit) {
    // First request from this user
    store.set(key, { count: 1, resetTime: now + windowMs })
    return true
  }
  
  if (now > userLimit.resetTime) {
    // Reset window expired
    store.set(key, { count: 1, resetTime: now + windowMs })
    return true
  }
  
  if (userLimit.count >= limit) {
    // Rate limit exceeded
    return false
  }
  
  // Increment count
  userLimit.count++
  return true
}

// Rate limiting for password reset by email
const checkRateLimitByEmail = (email, limit = 3, windowMs = 3600000) => { // 1 hour default
  const now = Date.now()
  const store = rateLimits.passwordReset
  
  const emailLimit = store.get(email)
  
  if (!emailLimit) {
    // First request for this email
    store.set(email, { count: 1, resetTime: now + windowMs })
    return true
  }
  
  if (now > emailLimit.resetTime) {
    // Reset window expired
    store.set(email, { count: 1, resetTime: now + windowMs })
    return true
  }
  
  if (emailLimit.count >= limit) {
    // Rate limit exceeded
    return false
  }
  
  // Increment count
  emailLimit.count++
  return true
}

// Rate limiting for signup by IP address
const checkRateLimitByIP = (ip, limit = 10, windowMs = 3600000) => { // 10 per hour default
  const now = Date.now()
  const store = rateLimits.ipSignup
  
  const ipLimit = store.get(ip)
  
  if (!ipLimit) {
    // First request from this IP
    store.set(ip, { count: 1, resetTime: now + windowMs })
    return true
  }
  
  if (now > ipLimit.resetTime) {
    // Reset window expired
    store.set(ip, { count: 1, resetTime: now + windowMs })
    return true
  }
  
  if (ipLimit.count >= limit) {
    // Rate limit exceeded
    return false
  }
  
  // Increment count
  ipLimit.count++
  return true
}

// Get user's IP address from external service
const getUserIP = async () => {
  try {
    // Using ipapi.co as it's reliable and free
    const response = await fetch('https://ipapi.co/ip/', {
      method: 'GET',
      headers: {
        'Accept': 'text/plain'
      }
    })
    
    if (response.ok) {
      const ip = await response.text()
      return ip.trim()
    }
  } catch (error) {
    console.warn('Could not fetch IP address:', error)
  }
  
  // Fallback: generate a browser fingerprint as IP substitute
  // This is less reliable but better than no protection
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  ctx.textBaseline = 'top'
  ctx.font = '14px Arial'
  ctx.fillText('IP fallback fingerprint', 2, 2)
  
  const fingerprint = canvas.toDataURL().slice(-50) // Last 50 chars as pseudo-IP
  return `fp_${fingerprint}`
}

// Auth helpers
export const signUp = async (email, password) => {
  // Get user's IP address for rate limiting
  const userIP = await getUserIP()
  
  // Check IP rate limit: 10 signups per hour per IP
  if (!checkRateLimitByIP(userIP, 10, 3600000)) {
    return { 
      data: null, 
      error: { 
        message: 'Too many account creation attempts from this location. Please try again in 1 hour.' 
      } 
    }
  }
  
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
  })
  return { data, error }
}

export const signIn = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  })
  return { data, error }
}

export const signOut = async () => {
  const { error } = await supabase.auth.signOut()
  return { error }
}

export const getCurrentUser = () => {
  return supabase.auth.getUser()
}

// Task operations
export const getTasks = async () => {
  const { data, error } = await supabase
    .from('tasks')
    .select(`
      *,
      subtasks (*),
      task_shares (
        shared_with_email,
        shared_by_user_id
      )
    `)
    .order('created_at', { ascending: false })
  
  return { data, error }
}

export const createTask = async (task) => {
  // Get current user for rate limiting
  const { data: { user } } = await supabase.auth.getUser()
  
  if (!user) {
    return { data: null, error: { message: 'User not authenticated' } }
  }
  
  // Check rate limit (10 tasks per minute)
  if (!checkRateLimit(user.id, 'tasks', 10)) {
    return { data: null, error: { message: 'Rate limit exceeded. Maximum 10 tasks per minute.' } }
  }
  
  const { data, error } = await supabase
    .from('tasks')
    .insert([task])
    .select()
  
  return { data, error }
}

export const updateTask = async (id, updates) => {
  const { data, error } = await supabase
    .from('tasks')
    .update(updates)
    .eq('id', id)
    .select()
  
  return { data, error }
}

export const deleteTask = async (id) => {
  const { error } = await supabase
    .from('tasks')
    .delete()
    .eq('id', id)
  
  return { error }
}

// Subtask operations
export const createSubtask = async (subtask) => {
  const { data, error } = await supabase
    .from('subtasks')
    .insert([subtask])
    .select()
  
  return { data, error }
}

export const updateSubtask = async (id, updates) => {
  const { data, error } = await supabase
    .from('subtasks')
    .update(updates)
    .eq('id', id)
    .select()
  
  return { data, error }
}

export const deleteSubtask = async (id) => {
  const { error } = await supabase
    .from('subtasks')
    .delete()
    .eq('id', id)
  
  return { error }
}

// Task sharing
export const shareTask = async (taskId, email) => {
  // Get current user for rate limiting
  const { data: { user } } = await supabase.auth.getUser()
  
  if (!user) {
    return { data: null, error: { message: 'User not authenticated' } }
  }
  
  // Check rate limit (10 shares per minute)
  if (!checkRateLimit(user.id, 'shares', 10)) {
    return { data: null, error: { message: 'Rate limit exceeded. Maximum 10 shares per minute.' } }
  }
  
  const { data, error } = await supabase
    .from('task_shares')
    .insert([{
      task_id: taskId,
      shared_with_email: email
    }])
    .select()
  
  return { data, error }
}

export const unshareTask = async (taskId, email) => {
  const { error } = await supabase
    .from('task_shares')
    .delete()
    .eq('task_id', taskId)
    .eq('shared_with_email', email)
  
  return { error }
}

// Remove current user from shared task
export const removeFromSharedTask = async (taskId, userEmail) => {
  const { error } = await supabase
    .from('task_shares')
    .delete()
    .eq('task_id', taskId)
    .eq('shared_with_email', userEmail)
  
  return { error }
}

// Password Recovery
export const requestPasswordReset = async (email) => {
  // Check rate limit: 3 requests per hour per email
  if (!checkRateLimitByEmail(email, 3, 3600000)) { // 1 hour
    return { 
      data: null, 
      error: { message: 'Too many reset attempts. Please try again in 1 hour.' },
      success: false
    }
  }
  
  try {
    await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    })
    
    // Always return same success message to prevent user enumeration
    return { 
      data: null, 
      error: null, 
      message: 'If an account exists with that email, you will receive a password reset link.',
      success: true
    }
  } catch (error) {
    console.error('Password reset error:', error)
    return { 
      data: null, 
      error, 
      message: 'If an account exists with that email, you will receive a password reset link.',
      success: true // Still return success to prevent enumeration
    }
  }
}

export const updatePassword = async (newPassword) => {
  const { data, error } = await supabase.auth.updateUser({ 
    password: newPassword 
  })
  
  return { data, error }
}